import React from 'react';
import * as ResourceApi from 'api/resources';
import IndexedSubSectionWrapper from "hoc/IndexedSubSectionWrapper";
import {createButtonSectionFactory} from "hoc/CreateButtonSection";
import {isNoU} from "utils/DBUtils";
import {FBCommentQuestionToolTip} from 'components/break/tooltips/breaks-steps-section-tool-tips';
import {FormattedMessage, IntlProvider} from 'react-intl';
import {enUSmessage} from 'i18n/en-US.js';
import {
    ArticleAddTrackingToVotingButtonsToolTip,
    ArticleAllowVotingToolTip,
    ArticleBlockquoteToolTip,
    ArticleBodyCopyToolTip,
    ArticleDownvoteButtonIconToolTip,
    ArticleDownvoteButtonTextToolTip,
    ArticleEmbedToolTip,
    ArticleHeaderImageToolTip,
    ArticleHeadlineToolTip,
    ArticleImageScalingToolTip,
    ArticleImageToolTip,
    ArticleIntroToolTip,
    ArticleSubheadToolTip,
    ArticleUpvoteButtonIconToolTip,
    ArticleUpvoteButtonTextToolTip
} from 'components/break/tooltips/article-tool-tips';

export default {
    name: "article",
    subSection: true,
    resource: true,
    hideIfResourceIsNoU: true,

    fields: [
        {
            type: 'contenteditable',
            name: 'headline',
            // Headline is only allowed if there is more than one step
            showField: (proxy) => (proxy.currentBaseResource.steps && proxy.currentBaseResource.steps.length > 1),
            linkedField: (formProxy) => ["title"],
            required: true,
            props: {
                labelKey: 'editbreak.steps.article.headline',
                helpContent:  <ArticleHeadlineToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.headline'}/></IntlProvider>
            }
        },
        {
          type: 'image',
          name: 'headerImageId',
          required: true,
          props: {
            labelKey: 'editbreak.steps.article.header_image',
            imageType: 'stepHeaderImage',
            helpContent:  <ArticleHeaderImageToolTip/>,
            helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.header_image'}/></IntlProvider>
          }
        },
        {
            type: 'contenteditable',
            name: 'introCopy',
            required: false,
            props: {
              labelKey: 'editbreak.steps.article.intro_copy',
              helpContent:  <ArticleIntroToolTip/>,
              helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.intro_copy'}/></IntlProvider>,
              maxLength: 500,
              placeholderKey: 'editbreak.steps.article.intro_copy.placeholder',
              validationRules: [
                  { type: 'MarkupTextLimit',
                    params: {
                      limit: 500
                    }
                }
              ]
            }
        },
        {
            type: 'text',
            name: 'commentQuestion',
            showField: (proxy) => (proxy.currentBaseResource.showFBComments && proxy.currentBaseResource.sponsoredType !== 'sponsored'),
            props: {
                labelKey: 'editbreak.steps.related_breaks.commentQuestion',
                helpContent: <FBCommentQuestionToolTip/>,
                helpHeader: <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'tooltips.break.breaks_steps.fbCommentQuestion'}/></IntlProvider>
            }
        },
        {
          conditionalSection: true,
          name: 'defaultVoting',
          required: true,
          props: {
            labelKey: 'editbreak.steps.article.has_voting',
            helpContent:  <ArticleAllowVotingToolTip/>,
            helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.has_voting'}/></IntlProvider>
          },
          fields: [
            {
                type: "text",
                name: "defaultUpvoteBtnText",
                required: true,
                props: {
                    labelKey: "editbreak.steps.article.default_upvote_text",
                    helpContent:  <ArticleUpvoteButtonTextToolTip/>,
                    helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.default_upvote_text'}/></IntlProvider>
                }
            },
            {
                type: "text",
                name: "defaultUpvoteBtnIcon",
                required: true,
                props: {
                    labelKey: "editbreak.steps.article.default_upvote_icon",
                    helpContent:  <ArticleUpvoteButtonIconToolTip/>,
                    helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.default_upvote_icon'}/></IntlProvider>
                }
            },
            {
                type: "text",
                name: "defaultDownvoteBtnText",
                required: true,
                props: {
                    labelKey: "editbreak.steps.article.default_downvote_text",
                    helpContent:  <ArticleDownvoteButtonTextToolTip/>,
                  helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.default_downvote_text'}/></IntlProvider>
                }
            },
            {
                type: "text",
                name: "defaultDownvoteBtnIcon",
                required: true,
                props: {
                    labelKey: "editbreak.steps.article.default_downvote_icon",
                    helpContent:  <ArticleDownvoteButtonIconToolTip/>,
                    helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.default_downvote_icon'}/></IntlProvider>
                }
            },
            {
              conditionalSection: true,
              name: 'hasVotingEndDate',
              required: true,
              props: {
                labelKey: 'editbreak.steps.article.has_voting_end_date'
              },
              fields: [
                {
                    type: "datetime",
                    name: "votingEndDateTime",
                    required: true,
                    props: {
                        labelKey: "editbreak.steps.article.voting_end_date"
                    }
                }
              ]
            },  // end conditional section for hasVotingEndDate
            {
                conditionalSection: true,
                name: "hasVoteTracking",
                required: true,
                showField: (proxy) =>  (proxy.currentResource.defaultVoting === true),
                props: {
                    labelKey: 'editbreak.steps.article.has_vote_tracking',
                    helpContent:  <ArticleAddTrackingToVotingButtonsToolTip/>,
                    helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.has_vote_tracking'}/></IntlProvider>
                },

                fields: [
                  {
                      type: "text",
                      name: "upvoteTags",
                      required: true,
                      props: {
                          labelKey: "editbreak.steps.article.upvote_tags",
                          asList: true
                      }
                  },
                  {
                      type: "text",
                      name: "downvoteTags",
                      required: true,
                      props: {
                          labelKey: "editbreak.steps.article.downvote_tags",
                          asList: true
                      }
                  }
                ]
            }  // end conditional section for allow voting on item
          ]
        },
        {
          name: 'contents',
          subSection: true,
          resource: true,
          collection: true,
          sectionWrapper: IndexedSubSectionWrapper,
          deletable: true,

          props: {
              getCustomSectionHeader: (content, sectionIndex) => {
                // TODO: Other types
                if ( content.type === 'Subhead' && content.subHead && content.subHead.trim() !== "") {
                  return 'Subhead ' + (sectionIndex+1) + ': ' + content.subHead;
                } else if (content.type === "Menu" && content.text && content.text.trim() !== "") {
                    return "Menu " + (sectionIndex+1) + ': ' + content.text;
                }

                return content.type;
              },
          },
          footerContent: createButtonSectionFactory(
            ResourceApi.Types.ArticleSubhead,
            ResourceApi.Types.MenuContent,
            ResourceApi.Types.ArticleImage,
            ResourceApi.Types.ArticleBodyCopy,
            ResourceApi.Types.ArticleBlockQuote,
            ResourceApi.Types.ArticleEmbedContent,
            ResourceApi.Types.ArticleLinkButton,
            ResourceApi.Types.ArticleImageComparison
          ),

            sortable: true,
            sortableName: "articleContentsOrder",
            toggleSortLabelKey: 'editbreak.steps.article.content.reorder_toggle',
            resourceType: ResourceApi.Types.ListicleContent,
          fields: [
            {
              name: 'subHead',
              type: 'contenteditable',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'Subhead'),
              props: {
                labelKey: 'editbreak.steps.article.subhead',
                helpContent:  <ArticleSubheadToolTip/>,
                helpHeader: 'editbreak.steps.article.subhead'
              }
            },
              //
              // Menu Content
              //
              {
                  name: 'text',
                  type: 'text',
                  required: true,
                  showField: (proxy) => (proxy.currentResource.type === 'Menu'),
                  props: {
                      labelKey: 'editbreak.steps.article.menucontent.text',
                      // helpContent:  <ArticleSubheadToolTip/>,
                      // helpHeader: 'editbreak.steps.article.subhead'
                  }
              },
              {
                  name: 'items',
                  subSection: true,
                  resource: true,
                  collection: true,
                  sectionWrapper: IndexedSubSectionWrapper,
                  deletable: true,
                  canDelete: (formProxy) => {
                      const parent = formProxy.parentCurrentResource;
                      return parent.items && parent.items.length > 2;
                  },
                  canCreate: (formProxy) => {
                      const parent = formProxy.parentCurrentResource;
                      return parent.items && parent.items.length < 15;
                  },

                  props: {
                      getCustomSectionHeader: (content, sectionIndex) => {
                          if ( content.label && content.label.trim() !== "") {
                              return 'Menu Item ' + (sectionIndex+1) + ': ' + content.label;
                          }

                          return 'Menu Item';
                      },
                  },
                  footerContent: createButtonSectionFactory(
                      ResourceApi.Types.MenuContentItem
                  ),

                  sortable: true,
                  sortableName: "menuItemsOrder",
                  toggleSortLabelKey: 'editbreak.steps.article.content.reorder_toggle',
                  resourceType: ResourceApi.Types.MenuContentItem,

                  fields: [
                      {
                          name: 'label',
                          type: 'text',
                          required: true,
                          props: {
                              labelKey: 'editbreak.steps.article.menucontent.item.label'
                          }
                      },
                      {
                          name: 'link',
                          type: 'text',
                          required: true,
                          props: {
                              labelKey: 'editbreak.steps.article.menucontent.item.link',
                              starterValue: "https://",
                              validationRules: [
                                  {
                                      type: 'HyperLinkFormat'
                                  }
                              ]
                          }
                      },
                      // {
                      //     name: 'trackingId',
                      //     type: 'text',
                      //     required: true,
                      //     props: {
                      //         labelKey: 'editbreak.steps.article.menucontent.item.trackingId',
                      //     }
                      // },
                      {
                          name: 'imageId',
                          type: 'image',
                          idFor: 'image',
                          props: {
                              labelKey: 'editbreak.steps.article.menucontent.item.image',
                              imageType: 'menuContentIcon',
                          }
                      },
                  ]
              },
              //
              // End Menu Content
              //
            {
              name: 'imageType',
              type: 'dropdown',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'Image'),
              props: {
                labelKey: 'editbreak.steps.article.image.type',
                options: [
                  {
                    value: 'CUSTOM',
                    textKey: 'editbreak.steps.article.image.type.custom'
                  },
                  {
                    value: 'HORIZONTAL',
                    textKey: 'editbreak.steps.article.image.type.horizontal'
                  },
                  {
                    value: 'VERTICAL',
                    textKey: 'editbreak.steps.article.image.type.vertical'
                  }
                ]
              }
            },
            {
              name: 'scaling',
              type: 'radio',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'Image' && !isNoU(proxy.currentResource.imageType)),
              props: {
                labelKey: 'editbreak.steps.article.image.scaling',
                helpContent:  <ArticleImageScalingToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.image.scaling'}/></IntlProvider>,
                options: [
                  {
                    value: 100,
                    textKey: 'editbreak.steps.article.image.scaling.none'
                  },
                  {
                    value: 65,
                    textKey: 'editbreak.steps.article.image.scaling.65'
                  },
                  {
                    value: 75,
                    textKey: 'editbreak.steps.article.image.scaling.75'
                  }
                ]
              }
            },
            // TODO: Update imageType prop to be a method and not need multiple instances
            {
              name: 'imageId',
              type: 'image',
              idFor: 'image',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'Image' && proxy.currentResource.imageType === 'CUSTOM'),
              props: {
                labelKey: 'editbreak.steps.article.image.image',
                imageType: 'custom',
                helpContent:  <ArticleImageToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.image.image'}/></IntlProvider>
              }
            },
            {
              name: 'imageId',
              type: 'image',
              idFor: 'image',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'Image' && proxy.currentResource.imageType === 'HORIZONTAL'),
              props: {
                labelKey: 'editbreak.steps.article.image.image',
                imageType: 'listicleHorizontal',
                helpContent:  <ArticleImageToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.image.image'}/></IntlProvider>
              }
            },
            {
              name: 'imageId',
              type: 'image',
              idFor: 'image',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'Image' && proxy.currentResource.imageType === 'VERTICAL'),
              props: {
                labelKey: 'editbreak.steps.article.image.image',
                imageType: 'listicleVertical',
                helpContent:  <ArticleImageToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.image.image'}/></IntlProvider>
              }
            },
            {
              name: 'blockQuote',
              type: 'contenteditable',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'BlockQuote'),
              props: {
                labelKey: 'editbreak.steps.article.block_quote.value',
                placeholderKey: 'editbreak.steps.article.block_quote.value.placeholder',
                helpContent:  <ArticleBlockquoteToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.block_quote.value'}/></IntlProvider>
              }
            },
            {
              name: 'blockQuoteSource',
              type: 'contenteditable',
              required: false,
              showField: (proxy) => (proxy.currentResource.type === 'BlockQuote'),
              props: {
                labelKey: 'editbreak.steps.article.block_quote_source.value',
                placeholderKey: 'editbreak.steps.article.block_quote_source.value.placeholder'
              }
            },
            {
              name: 'bodyCopy',
              type: 'contenteditable',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'BodyCopy'),
              props: {
                labelKey: 'editbreak.steps.article.body_copy.value',
                placeholderKey: 'editbreak.steps.article.body_copy.value.placeholder',
                helpContent:  <ArticleBodyCopyToolTip/>,
                helpHeader: 'editbreak.steps.article.body_copy.value'
              }
            },
            {
              name: 'content',
              type: 'contenteditable',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'OldBodyCopy'),
              props: {
                labelKey: 'editbreak.steps.article.old_body_copy.value'
              }
            },
            {
              name: 'embedCode',
              type: 'textarea',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'EmbedCode'),
              props: {
                labelKey: 'editbreak.steps.article.embed_code.value',
                placeholderKey: 'editbreak.steps.article.embed_code.value.placeholder',
                helpContent:  <ArticleEmbedToolTip/>,
                helpHeader:   <IntlProvider locale="en" messages={enUSmessage}><FormattedMessage id={'editbreak.steps.article.embed_code.value'}/></IntlProvider>
              }
            },
            {
              name: 'buttonText',
              type: 'text',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'Button'),
              props: {
                labelKey: 'editbreak.steps.article.button.text'
              }
            },
            {
              name: 'url',
              type: 'text',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'Button'),
              props: {
                labelKey: 'editbreak.steps.article.button.url'
              }
            },
            {
              name: 'textColor',
              type: 'color',
              required: false,
              showField: (proxy) => (proxy.currentResource.type === 'Button'),
              props: {
                labelKey: 'editbreak.steps.article.button.text_color'
              }
            },
            {
              name: 'bgColor',
              type: 'color',
              required: false,
              showField: (proxy) => (proxy.currentResource.type === 'Button'),
              props: {
                labelKey: 'editbreak.steps.article.button.bg_color'
              }
            },
            {
              name: 'leftImageId',
              type: 'image',
              idFor: 'leftImage',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'ImageComparison'),
              props: {
                labelKey: 'editbreak.steps.article.image_comparison.left_image',
                imageType: 'listicleImageComparison'
              }
            },
            {
              name: 'rightImageId',
              type: 'image',
              idFor: 'rightImage',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'ImageComparison'),
              props: {
                labelKey: 'editbreak.steps.article.image_comparison.right_image',
                imageType: 'listicleImageComparison'
              }
            },
            {
              name: 'startPosition',
              type: 'dropdown',
              required: true,
              showField: (proxy) => (proxy.currentResource.type === 'ImageComparison'),
              props: {
                labelKey: 'editbreak.steps.article.image_comparison.start_position',
                options: [
                  {
                    value: 'LEFT',
                    textKey: 'editbreak.steps.article.image_comparison.start_position.left'
                  },
                  {
                    value: 'CENTER',
                    textKey: 'editbreak.steps.article.image_comparison.start_position.center'
                  },
                  {
                    value: 'RIGHT',
                    textKey: 'editbreak.steps.article.image_comparison.start_position.right'
                  }
                ]
              }
            }
          ]
        } // end contents
    ]
}



// WEBPACK FOOTER //
// ./public/components/EditContent/configs/breaks/steps/article.js